// VerificationPage: per-condition status + the four-state certificate.
//
// The four-state certificate (VERIFIED / CORROBORATED / CONTRADICTED /
// UNTESTABLE) is the source of truth; A/AA/AAA is a *derived presentation
// view* over it (CLAUDE.md "Four-state certificate" invariant).
//
// The user runs ops here:
//   - Assert structural integrity (bulk Phase-1 + n/a-asserted).
//   - Compute verification level (rollup).
//
// And sees:
//   - The certificate summary prominently (per-state counts + fail-closed).
//   - The derived A/AA/AAA level as a secondary badge.
//   - A table of every recorded condition with its four-state, warrant,
//     and detail.
//
// Per-condition attestation ops (AttestGrainUniqueness, AttestFDEdge,
// AttestSiblingCoherence) need specific inputs (table / column pair / etc.)
// — they get UI in subsequent slices. This page focuses on the two
// session-wide rollup operations + the displayed result.

import { useCallback, useEffect, useState } from "react";
import { ApiError, type ConditionsResponse } from "../api/client";
import type {
  AssertStructuralIntegrityResult,
  CertificateState,
  ComputeVerificationLevelResult,
  Level,
} from "../api/types";
import { useSession } from "./SessionContext";

export function VerificationPage() {
  const { client, session, refreshSession } = useSession();
  const [conditions, setConditions] = useState<ConditionsResponse | null>(null);
  const [lastLevelResult, setLastLevelResult] =
    useState<ComputeVerificationLevelResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadConditions = useCallback(async () => {
    if (session === null) return;
    try {
      const c = await client.getConditions(session.session_id);
      setConditions(c);
    } catch (e) {
      setError(formatError(e));
    }
  }, [client, session?.session_id]);

  useEffect(() => {
    setConditions(null);
    setLastLevelResult(null);
    setError(null);
    if (session !== null) {
      loadConditions();
    }
  }, [session, loadConditions]);

  async function handleAssertStructural() {
    if (session === null) return;
    setBusy(true);
    setError(null);
    try {
      const r = await client.runOp(session.session_id, "AssertStructuralIntegrity", {
        inputs: {},
      });
      if (!r.success || !r.result) {
        throw new Error(r.error ?? "AssertStructuralIntegrity failed");
      }
      // Result currently unused beyond refreshing; surface via a flash msg if needed.
      const result = r.result as unknown as AssertStructuralIntegrityResult;
      void result;
      await loadConditions();
      refreshSession();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleComputeLevel() {
    if (session === null) return;
    setBusy(true);
    setError(null);
    try {
      const r = await client.runOp(session.session_id, "ComputeVerificationLevel", {
        inputs: {},
      });
      if (!r.success || !r.result) {
        throw new Error(r.error ?? "ComputeVerificationLevel failed");
      }
      setLastLevelResult(r.result as unknown as ComputeVerificationLevelResult);
      await loadConditions();
      refreshSession();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy(false);
    }
  }

  if (session === null) {
    return (
      <section>
        <h1>Verify</h1>
        <p style={{ color: "#888" }}>
          Create a session on the <em>Session</em> tab first.
        </p>
      </section>
    );
  }

  const conditionEntries = Object.entries(conditions?.condition_statuses ?? {});
  const certificate = conditions?.certificate ?? null;
  const certStates = certificate?.states ?? {};
  // Derived A/AA/AAA view: prefer the certificate's freshly-derived level,
  // fall back to the op result / stored level for older backends.
  const level =
    certificate?.level ??
    lastLevelResult?.level ??
    conditions?.last_computed_level ??
    null;

  return (
    <section>
      <h1>Verify</h1>
      <p style={subtitleStyle}>
        The four-state certificate (VERIFIED / CORROBORATED / CONTRADICTED /
        UNTESTABLE) is the source of truth; A/AA/AAA is a derived view. Run
        the two session-wide ops below; per-condition attestation UI lands
        later.
      </p>

      {certificate?.fail_closed && (
        <FailClosedBanner ids={certificate.blocking_contradictions} />
      )}

      <CertificateSummary states={certStates} />

      <div style={metricsRowStyle}>
        <LevelBadge level={level} />
        {lastLevelResult && (
          <>
            <Metric label="total in catalog" value={String(lastLevelResult.total_conditions)} />
            <Metric label="unattempted" value={String(lastLevelResult.unattempted_conditions)} />
          </>
        )}
      </div>

      <div style={actionsRowStyle}>
        <button onClick={handleAssertStructural} disabled={busy} style={buttonStyle}>
          Assert structural integrity
        </button>
        <button onClick={handleComputeLevel} disabled={busy} style={buttonStyle}>
          Compute verification level
        </button>
      </div>

      {error && (
        <div style={errorBlockStyle}>
          <strong>Error:</strong> {error}
        </div>
      )}

      <h2 style={{ marginTop: "1.5rem" }}>Condition statuses</h2>
      {conditionEntries.length === 0 ? (
        <p style={{ color: "#888" }}>
          No condition statuses recorded yet. Run <em>Assert structural integrity</em>{" "}
          to populate Phase-1 + n/a-asserted conditions.
        </p>
      ) : (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>id</th>
              <th style={thStyle}>certificate</th>
              <th style={thStyle}>warrant</th>
              <th style={thStyle}>detail</th>
            </tr>
          </thead>
          <tbody>
            {conditionEntries
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([cid, status]) => (
                <tr key={cid}>
                  <td style={tdStyle}>
                    <code>{cid}</code>
                  </td>
                  <td style={tdStyle}>
                    <CertificateBadge state={certStates[cid] ?? null} status={status} />
                  </td>
                  <td style={tdStyle}>
                    {status.warrant_used ? (
                      <span style={warrantBadgeStyle(status.warrant_used)}>
                        {status.warrant_used}
                      </span>
                    ) : (
                      <span style={{ color: "#aaa" }}>—</span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {status.detail ? (
                      <span style={{ color: "#666" }}>{status.detail}</span>
                    ) : (
                      <span style={{ color: "#ccc" }}>—</span>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

// ── Sub-components ───────────────────────────────────────────────────

function LevelBadge({ level }: { level: Level | null }) {
  const palette: Record<string, { bg: string; fg: string; border: string; label: string }> = {
    A: { bg: "#fff7e6", fg: "#8b6914", border: "#e6d8a8", label: "A" },
    AA: { bg: "#e6f0ff", fg: "#1c4e80", border: "#bcd5f0", label: "AA" },
    AAA: { bg: "#e6ffed", fg: "#1a6b3a", border: "#a5d8b8", label: "AAA" },
  };
  const variant = level ? palette[level] : null;
  return (
    <div style={levelBadgeOuterStyle}>
      <div style={levelLabelStyle}>verification level</div>
      <div style={levelValueStyle(variant)}>
        {variant !== null ? variant.label : "—"}
      </div>
      {level === null && (
        <div style={levelHintStyle}>
          Not yet computed (or below Level A — some always-blocking condition
          unsatisfied / failed).
        </div>
      )}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div style={metricStyle}>
      <div style={metricLabelStyle}>{label}</div>
      <div style={metricValueStyle}>{value}</div>
    </div>
  );
}

// The four certificate states, in display order, with their palette and a
// one-line gloss. This is the single place the four-state vocabulary is
// rendered — keep it aligned with coframe.installation.CertificateState.
const CERT_STATE_META: Record<
  CertificateState,
  { bg: string; fg: string; border: string; gloss: string }
> = {
  VERIFIED: {
    bg: "#e6ffed",
    fg: "#1a6b3a",
    border: "#a5d8b8",
    gloss: "data-derivable answer computed and holds",
  },
  CORROBORATED: {
    bg: "#e6f6fb",
    fg: "#0f6b80",
    border: "#a8d8e6",
    gloss: "asserted; a detection battery found no contradiction",
  },
  CONTRADICTED: {
    bg: "#fde9e9",
    fg: "#a02020",
    border: "#f3c0c0",
    gloss: "a positive finding contradicts the declaration (fail-closed)",
  },
  UNTESTABLE: {
    bg: "#fff7e6",
    fg: "#8b6914",
    border: "#e6d8a8",
    gloss: "asserted; no battery can test it — absence is never certified",
  },
};

const CERT_STATE_ORDER: CertificateState[] = [
  "VERIFIED",
  "CORROBORATED",
  "CONTRADICTED",
  "UNTESTABLE",
];

// Banner shown when the certificate is fail-closed: one or more blocking
// conditions are CONTRADICTED. Safety asymmetry — this must be loud and
// must never be silently downgradable.
function FailClosedBanner({ ids }: { ids: string[] }) {
  return (
    <div style={failClosedBannerStyle}>
      <strong>Fail-closed:</strong> {ids.length} blocking condition
      {ids.length === 1 ? " is" : "s are"} <code>CONTRADICTED</code>. A
      consumer must refuse to clear this AC by default.
      {ids.length > 0 && (
        <ul style={failClosedListStyle}>
          {ids.map((id) => (
            <li key={id}>
              <code>{id}</code>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Per-state counts over the assessed conditions, rendered as a row of
// chips. Not-attempted conditions are omitted from `states`, so the
// counts here are over *assessed* conditions only.
function CertificateSummary({
  states,
}: {
  states: Record<string, CertificateState>;
}) {
  const counts: Record<CertificateState, number> = {
    VERIFIED: 0,
    CORROBORATED: 0,
    CONTRADICTED: 0,
    UNTESTABLE: 0,
  };
  for (const s of Object.values(states)) {
    if (s in counts) counts[s] += 1;
  }
  const assessed = Object.keys(states).length;
  return (
    <div style={certSummaryOuterStyle}>
      <div style={certSummaryHeaderStyle}>
        certificate — {assessed} condition{assessed === 1 ? "" : "s"} assessed
      </div>
      <div style={certSummaryChipsStyle}>
        {CERT_STATE_ORDER.map((state) => {
          const meta = CERT_STATE_META[state];
          return (
            <div key={state} style={certSummaryChipStyle(meta)} title={meta.gloss}>
              <span style={certSummaryCountStyle}>{counts[state]}</span>
              <span>{state}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// A single condition's four-state badge. When the backend supplied a
// four-state value we render it; for an older backend that only sent
// ConditionStatus we fall back to the legacy failed / satisfied / —
// presentation so the page degrades gracefully.
function CertificateBadge({
  state,
  status,
}: {
  state: CertificateState | null;
  status: { failed: boolean; warrant_used: string | null };
}) {
  if (state !== null) {
    const meta = CERT_STATE_META[state];
    return (
      <span style={certBadgeStyle(meta)} title={meta.gloss}>
        {state}
      </span>
    );
  }
  // Legacy fallback (no certificate in payload).
  const legacy = status.failed
    ? { bg: "#fde9e9", fg: "#a02020", label: "failed" }
    : status.warrant_used
      ? { bg: "#e9f5e9", fg: "#1a6b3a", label: "satisfied" }
      : { bg: "#f0f0f0", fg: "#888", label: "—" };
  return (
    <span
      style={{
        background: legacy.bg,
        color: legacy.fg,
        padding: "0.1rem 0.4rem",
        borderRadius: 3,
        fontSize: "0.75rem",
      }}
    >
      {legacy.label}
    </span>
  );
}

function formatError(e: unknown): string {
  if (e instanceof ApiError) return `${e.status} ${e.message}`;
  if (e instanceof Error) return e.message;
  return String(e);
}

// ── Styles ────────────────────────────────────────────────────────────

const subtitleStyle: React.CSSProperties = { color: "#888", margin: "0 0 1rem 0" };
const actionsRowStyle: React.CSSProperties = {
  display: "flex",
  gap: "0.5rem",
  marginTop: "1rem",
};
const buttonStyle: React.CSSProperties = {
  padding: "0.5rem 1rem",
  background: "#0366d6",
  color: "white",
  border: "none",
  borderRadius: 4,
  cursor: "pointer",
  fontSize: "0.95rem",
};
const errorBlockStyle: React.CSSProperties = {
  padding: "0.6rem 0.8rem",
  background: "#ffeef0",
  border: "1px solid #f3c0c0",
  borderRadius: 4,
  marginTop: "1rem",
  fontSize: "0.9rem",
};
const metricsRowStyle: React.CSSProperties = {
  display: "flex",
  gap: "1.5rem",
  marginTop: "1rem",
};
const metricStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
};
const metricLabelStyle: React.CSSProperties = {
  fontSize: "0.7rem",
  color: "#888",
  textTransform: "uppercase",
  letterSpacing: "0.02em",
};
const metricValueStyle: React.CSSProperties = {
  fontSize: "0.95rem",
  fontWeight: 500,
};
const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "0.9rem",
};
const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "0.4rem 0.6rem",
  background: "#fafafa",
  borderBottom: "2px solid #ddd",
  fontSize: "0.8rem",
  color: "#666",
  fontWeight: 500,
};
const tdStyle: React.CSSProperties = {
  padding: "0.35rem 0.6rem",
  borderBottom: "1px solid #eee",
  verticalAlign: "top",
};

// ── Level badge styles ────────────────────────────────────────────────

const levelBadgeOuterStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "0.25rem",
  padding: "1rem 1.5rem",
  background: "#fafafa",
  border: "1px solid #ddd",
  borderRadius: 6,
  marginTop: "0.5rem",
  marginBottom: "1rem",
  width: "fit-content",
  minWidth: 220,
};
const levelLabelStyle: React.CSSProperties = {
  fontSize: "0.7rem",
  color: "#888",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

function levelValueStyle(
  variant: { bg: string; fg: string; border: string } | null,
): React.CSSProperties {
  return {
    fontSize: "2.5rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    color: variant?.fg ?? "#bbb",
    background: variant?.bg ?? "transparent",
    border: variant ? `2px solid ${variant.border}` : "2px dashed #ddd",
    borderRadius: 6,
    padding: "0.25rem 1rem",
    width: "fit-content",
    fontFamily: "system-ui",
    lineHeight: 1,
  };
}

const levelHintStyle: React.CSSProperties = {
  fontSize: "0.8rem",
  color: "#888",
  maxWidth: "30rem",
};

function warrantBadgeStyle(warrant: string): React.CSSProperties {
  const palette: Record<string, [string, string]> = {
    "author-assertion": ["#e3edf9", "#1c4e80"],
    "code-affirmation": ["#fef2d6", "#8b6914"],
    "data-attestation": ["#e9f5e9", "#1a6b3a"],
    "catalog": ["#f4ecf9", "#5c2780"],
    "opted-out": ["#f0f0f0", "#666"],
  };
  const [bg, fg] = palette[warrant] ?? ["#f0f0f0", "#666"];
  return {
    background: bg,
    color: fg,
    padding: "0.1rem 0.4rem",
    borderRadius: 3,
    fontSize: "0.75rem",
  };
}

// ── Certificate styles ───────────────────────────────────────────────

const failClosedBannerStyle: React.CSSProperties = {
  padding: "0.7rem 0.9rem",
  background: "#fde9e9",
  border: "2px solid #e0a0a0",
  borderRadius: 6,
  marginBottom: "1rem",
  fontSize: "0.9rem",
  color: "#7a1818",
};
const failClosedListStyle: React.CSSProperties = {
  margin: "0.4rem 0 0 0",
  paddingLeft: "1.2rem",
};

const certSummaryOuterStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "0.5rem",
  padding: "0.8rem 1rem",
  background: "#fafafa",
  border: "1px solid #ddd",
  borderRadius: 6,
  marginTop: "0.5rem",
};
const certSummaryHeaderStyle: React.CSSProperties = {
  fontSize: "0.7rem",
  color: "#888",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};
const certSummaryChipsStyle: React.CSSProperties = {
  display: "flex",
  gap: "0.6rem",
  flexWrap: "wrap",
};

function certSummaryChipStyle(meta: {
  bg: string;
  fg: string;
  border: string;
}): React.CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    gap: "0.4rem",
    background: meta.bg,
    color: meta.fg,
    border: `1px solid ${meta.border}`,
    borderRadius: 4,
    padding: "0.3rem 0.6rem",
    fontSize: "0.8rem",
    fontWeight: 500,
  };
}

const certSummaryCountStyle: React.CSSProperties = {
  fontSize: "1.1rem",
  fontWeight: 700,
};

function certBadgeStyle(meta: {
  bg: string;
  fg: string;
  border: string;
}): React.CSSProperties {
  return {
    background: meta.bg,
    color: meta.fg,
    border: `1px solid ${meta.border}`,
    padding: "0.1rem 0.4rem",
    borderRadius: 3,
    fontSize: "0.75rem",
    fontWeight: 500,
  };
}
